import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import mdesLogo from "./img/mdes_logo.png";
import masterCardLogo from "./img/mastercard_logo.png";
import {
  Grid,
  Name,
  Photo,
  PhotoWrapper,
  PLACEHOLDER,
  Tile,
} from "./styles/CheckInUsersWallStyles";

import {
  FullscreenWrapper,
  GuestCard,
  GuestImage,
  GuestName,
  MainContainer,
  MastercardLogo,
  MdesLogo,
  WaitingContainer,
  WaitingText,
} from "./styles/CheckInUsersStyles";

const CheckedInGuestsWall = () => {
  const guestsRef = useRef([]);
  const lastGuestRef = useRef(null);

  const [checkedInGuests, setCheckedInGuests] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [spotlightedId, setSpotlightedId] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | spotlighting | winner
  const [photoSize, setPhotoSize] = useState(50);
  const [nameFontSize, setNameFontSize] = useState(10);
  const pollingActive = useRef(true);
  const pollingIntervalTime = 500;
  const containerRef = useRef(null);

  // --- Spotlight randomizer ---
  const startSpotlight = () => {
    if (guestsRef.current.length === 0) return;

    setSelectedGuest(null);
    setPhase("spotlighting");

    let delay = 150;
    let elapsed = 0;
    const totalDuration = 10000;
    const slowDownStart = 6000;

    const spin = () => {
      const randomGuest =
        guestsRef.current[Math.floor(Math.random() * guestsRef.current.length)];
      setSpotlightedId(randomGuest.guestId);

      elapsed += delay;
      if (elapsed < totalDuration) {
        lastGuestRef.current = randomGuest;
        if (elapsed >= slowDownStart) delay = Math.min(delay + 100, 800);
        setTimeout(spin, delay);
      } else {
        setSelectedGuest(lastGuestRef.current);
        setPhase("winner");
      }
    };

    spin();
  };

  // --- Keyboard shortcuts ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key === "Enter") {
        toggleFullscreen();
        e.preventDefault();
      }
      if (e.altKey && e.code === "KeyR") {
        startSpotlight();
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen();
    else document.exitFullscreen();
  };

  // --- Poll API ---
  useEffect(() => {
    const pollQueue = async () => {
      if (!pollingActive.current) return;

      const url = `https://www.manage-mii.uk/api/das/guests/event-queue/mastercard_mdes_25/checked-in?currentCount=${guestsRef.current.length}`;

      try {
        const response = await axios.get(url);
        if (response.status >= 400) {
          if (pollingActive.current) setTimeout(pollQueue, 5000);
          return;
        }

        if (response.data && response.data.length > 0) {
          guestsRef.current = response.data;
          setCheckedInGuests(response.data);
        }
      } catch (err) {
        if (pollingActive.current) setTimeout(pollQueue, 5000);
      }

      if (pollingActive.current) setTimeout(pollQueue, pollingIntervalTime);
    };

    pollQueue();
    return () => {
      pollingActive.current = false;
    };
  }, []);

  // --- Dev-only test data ---
  const displayGuests =
    process.env.NODE_ENV === "developments" && checkedInGuests.length === 0
      ? Array.from({ length: 250 }, (_, i) => ({
          guestId: i + 1,
          guestFirstName: `Guest ${i + 1}`,
          guestPhotoURL: `https://picsum.photos/seed/${i}/200/200`,
        }))
      : checkedInGuests;

  // --- Compute photo & font size ---
  useEffect(() => {
    const updateSizes = () => {
      const totalGuests = displayGuests.length;
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const gap = 10;
      const maxGlobalPhotoSize = 200;

      const cols = Math.ceil(
        Math.sqrt(totalGuests * (screenWidth / screenHeight))
      );
      const rows = Math.ceil(totalGuests / cols);

      const maxWidth = (screenWidth - gap * (cols - 1)) / cols;
      const maxHeight = (screenHeight - gap * (rows - 1)) / rows - 20;

      const computedSize = Math.floor(Math.min(maxWidth, maxHeight));
      const finalPhotoSize = Math.min(computedSize, maxGlobalPhotoSize);

      setPhotoSize(Math.max(finalPhotoSize, 40));

      const font =
        finalPhotoSize >= 160
          ? 16
          : finalPhotoSize >= 140
          ? 14
          : finalPhotoSize >= 110
          ? 12
          : finalPhotoSize >= 90
          ? 11
          : 10;
      setNameFontSize(font);
    };

    updateSizes();
    window.addEventListener("resize", updateSizes);
    return () => window.removeEventListener("resize", updateSizes);
  }, [displayGuests.length]);

  // --- Winner animation ---
  if (selectedGuest)
    return (
      <FullscreenWrapper ref={containerRef}>
        <MainContainer>
          <AnimatePresence>
            <motion.div
              layoutId={`guest-${selectedGuest.guestId}`}
              onClick={() => {
                setPhase("idle");
                setSelectedGuest(null);
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            >
              <GuestCard>
                <GuestImage
                  src={selectedGuest.guestPhotoURL ?? PLACEHOLDER}
                  alt={selectedGuest.guestFirstName}
                />
                <GuestName>{selectedGuest.guestFirstName}</GuestName>
              </GuestCard>
            </motion.div>
          </AnimatePresence>
        </MainContainer>
      </FullscreenWrapper>
    );

  // --- Grid rendering with spotlight/fade ---
  return (
    <FullscreenWrapper ref={containerRef}>
      <Grid>
        <AnimatePresence>
          {displayGuests && displayGuests.length > 0 ? (
            displayGuests.map((guest) => {
              const isSpotlighted = spotlightedId === guest.guestId;
              const isWinner = selectedGuest?.guestId === guest.guestId;

              return (
                <motion.div
                  key={guest.guestId}
                  layoutId={`guest-${guest.guestId}`}
                  animate={{
                    opacity:
                      phase === "spotlighting"
                        ? isSpotlighted
                          ? 1
                          : 0.2
                        : phase === "winner"
                        ? isWinner
                          ? 1
                          : 0
                        : 1,
                    scale: isSpotlighted ? 1.2 : 1,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <Tile
                    onClick={() => setSelectedGuest(guest)}
                    style={{
                      border: isWinner
                        ? "6px solid gold"
                        : isSpotlighted
                        ? "3px solid #FD661B"
                        : "2px solid transparent",
                      borderRadius: "12px",
                      position: "relative",
                    }}
                  >
                    <PhotoWrapper size={photoSize}>
                      <Photo
                        src={guest.guestPhotoURL ?? PLACEHOLDER}
                        alt={guest.guestFirstName ?? "Guest photo"}
                        loading="lazy"
                      />
                    </PhotoWrapper>
                    <Name fontSize={nameFontSize} size={photoSize}>
                      {guest.guestFirstName ?? "Unknown"}
                    </Name>
                  </Tile>
                </motion.div>
              );
            })
          ) : (
            <MainContainer>
              <WaitingContainer>
                <MdesLogo
                  src={mdesLogo}
                  alt="mdesLogo"
                  onClick={() => toggleFullscreen()}
                />
                <MastercardLogo src={masterCardLogo} alt="mdesLogo" />

                <WaitingText>MDES Town Hall</WaitingText>
                <WaitingText>September 18, STL Cafeteria</WaitingText>
              </WaitingContainer>
            </MainContainer>
          )}
        </AnimatePresence>
      </Grid>
    </FullscreenWrapper>
  );
};

export default CheckedInGuestsWall;
